import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ipoService } from '../../api/ipoService';

const isFresh = (entry, maxAgeMs) => Boolean(maxAgeMs && entry?.fetchedAt && Date.now() - entry.fetchedAt < maxAgeMs);

// Ids not loaded recently and not already being fetched
const idsToFetch = (sliceState, ids, maxAgeMs) => {
  const inFlight = new Set(Object.values(sliceState.inFlight).flat());
  return [...new Set(ids.map(String))].filter((id) => !inFlight.has(id) && !isFresh(sliceState.byId[id], maxAgeMs));
};

/**
 * Broker reviews for a set of catalog IPOs
 * GET /api/core/ipos/{id}/broker-reviews for each id; a failed id is skipped.
 *
 * options.ids: catalog IPO ids
 * options.maxAgeMs: skip ids fetched this recently
 */
export const fetchBrokerReviews = createAsyncThunk(
  'brokerReviews/fetchBrokerReviews',
  async (_options, { getState, requestId }) => {
    // The pending reducer recorded which ids this request is responsible for
    const wanted = getState().brokerReviews.inFlight[requestId] || [];
    const results = await Promise.allSettled(wanted.map((id) => ipoService.getBrokerReviews(id)));
    const byId = {};
    results.forEach((result, idx) => {
      if (result.status === 'fulfilled' && result.value?.success !== false) {
        byId[wanted[idx]] = result.value?.data || null;
      }
    });
    return byId;
  },
  {
    condition: ({ ids = [], maxAgeMs = 0 } = {}, { getState }) => idsToFetch(getState().brokerReviews, ids, maxAgeMs).length > 0,
  }
);

const initialState = {
  // Keyed by catalog id: { data (the endpoint's data payload), fetchedAt }
  byId: {},
  // requestId -> ids that request is fetching
  inFlight: {},
};

export const brokerReviewsSlice = createSlice({
  name: 'brokerReviews',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBrokerReviews.pending, (state, action) => {
        const { ids = [], maxAgeMs = 0 } = action.meta.arg;
        state.inFlight[action.meta.requestId] = idsToFetch(state, ids, maxAgeMs);
      })
      .addCase(fetchBrokerReviews.fulfilled, (state, action) => {
        const fetchedAt = Date.now();
        Object.entries(action.payload).forEach(([id, data]) => {
          state.byId[id] = { data, fetchedAt };
        });
        delete state.inFlight[action.meta.requestId];
      })
      .addCase(fetchBrokerReviews.rejected, (state, action) => {
        delete state.inFlight[action.meta.requestId];
      });
  },
});

// True while any review request is running
export const selectBrokerReviewsLoading = (state) => Object.keys(state.brokerReviews.inFlight).length > 0;

export default brokerReviewsSlice.reducer;
